import api, { unwrap, unwrapList, unwrapOne } from "./apiClient";
import { BACKEND_URL } from "@/config/api";

const API_BASE = `${BACKEND_URL}/orders`;

/**
 * Status do pedido: a API envia/recebe o texto em português; o id numérico
 * aparece no WebSocket e em GET /orders/{code}/status.
 */
export const ORDER_STATUS = [
  { id: 0, name: "Aguardando", key: "OPEN" },
  { id: 1, name: "Preparando", key: "PROGRESS" },
  { id: 2, name: "Liberado", key: "FREE" },
  { id: 3, name: "Entregue", key: "DELIVERED" },
  { id: 4, name: "Finalizado", key: "CLOSE" },
  { id: 5, name: "Cancelado", key: "CANCEL" },
];

/**
 * Order Service (OrdersController - /orders)
 * Tudo é escopado pela empresa do token.
 */
class OrderService {
  /**
   * Todos os pedidos da empresa (com relações), do mais recente ao mais antigo
   * @param {string} [status] - texto do status (ex.: "Aguardando")
   * @param {object} [options] - config extra do axios (ex.: { silent: true })
   * @returns {Promise<Array>}
   */
  async getAdminOrders(status, options = {}) {
    try {
      const response = await api.get(`${API_BASE}/admin`, {
        ...options,
        params: status ? { status } : undefined,
      });
      return this.sortByDateDesc(unwrapList(response));
    } catch (error) {
      console.error("❌ Erro ao buscar pedidos:", error);
      throw error;
    }
  }

  /**
   * Quantidade de pedidos "Aguardando" da empresa (sem modal de erro:
   * usado em polling)
   * @returns {Promise<number>}
   */
  async countPending() {
    const response = await api.get(API_BASE, {
      params: { status: "Aguardando" },
      silent: true,
    });
    return unwrapList(response).length;
  }

  /**
   * @param {string} code
   * @returns {Promise<object|null>}
   */
  async getOrder(code) {
    try {
      const response = await api.get(`${API_BASE}/${code}`);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar pedido ${code}:`, error);
      throw error;
    }
  }

  /**
   * Atualiza o status e notifica via WebSocket
   * @param {string} code
   * @param {string} status - texto (ex.: "Preparando") ou nome do enum
   */
  async updateStatus(code, status) {
    try {
      const response = await api.put(`${API_BASE}/${code}/status`, { status });
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao atualizar status do pedido ${code}:`, error);
      throw error;
    }
  }

  /**
   * Envia o pedido para a impressora da loja (app Android da empresa)
   * @param {string} code
   */
  async print(code) {
    try {
      const response = await api.post(`${API_BASE}/${code}/print`);
      return unwrap(response);
    } catch (error) {
      console.error(`❌ Erro ao enviar pedido ${code} para impressão:`, error);
      throw error;
    }
  }

  /**
   * Notificação do pedido no Telegram
   * @param {string} code
   */
  async sendTelegram(code) {
    try {
      const response = await api.post(`${API_BASE}/telegram/${code}/send`);
      return unwrap(response);
    } catch (error) {
      console.error(`❌ Erro ao enviar pedido ${code} ao Telegram:`, error);
      throw error;
    }
  }

  /**
   * Status de pedido (GET /status, global)
   * @returns {Promise<Array<{id, name, key}>>}
   */
  async getStatuses() {
    try {
      const response = await api.get(`${BACKEND_URL}/status`);
      return unwrapList(response);
    } catch (error) {
      console.error("❌ Erro ao buscar status de pedido:", error);
      throw error;
    }
  }

  /**
   * Converte o status (texto, nome do enum ou id 0–5) para o texto da API
   * @param {string|number} value
   * @returns {string}
   */
  getStatusName(value) {
    if (value === null || value === undefined || value === "") return "";
    const text = String(value).trim();
    const found = ORDER_STATUS.find(
      (s) =>
        String(s.id) === text ||
        s.name.toLowerCase() === text.toLowerCase() ||
        s.key === text.toUpperCase(),
    );
    return found ? found.name : text;
  }

  /**
   * Converte o date_time do pedido em Date. A API envia "dd/MM/yyyy HH:mm:ss"
   * (formato que o `new Date()` não interpreta); ISO também é aceito.
   * @param {string} value
   * @returns {Date|null}
   */
  parseDateTime(value) {
    if (!value) return null;
    const match = String(value).match(
      /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/,
    );
    const date = match
      ? new Date(
          Number(match[3]),
          Number(match[2]) - 1,
          Number(match[1]),
          Number(match[4] || 0),
          Number(match[5] || 0),
          Number(match[6] || 0),
        )
      : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  /**
   * Total do pedido: itens − desconto do cupom + frete
   * @param {object} order
   * @returns {number}
   */
  calculateTotal(order) {
    const productsTotal = (order?.products || []).reduce(
      (sum, product) => sum + (parseFloat(product.total_value) || 0),
      0,
    );
    const discount =
      productsTotal * ((parseFloat(order?.discount_percentage) || 0) / 100);
    const freight = parseFloat(order?.freight_value) || 0;
    return productsTotal - discount + freight;
  }

  sortByDateDesc(orders) {
    const time = (order) => this.parseDateTime(order?.date_time)?.getTime() || 0;
    return [...orders].sort((a, b) => time(b) - time(a));
  }
}

export default new OrderService();
